import {
  EnvConfig,
  isAssetReadOnly,
  Media,
  MediaType,
  normalizeUrl,
  PermissionValues,
  useCurrentAccount,
  useProduct,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { MediaListItem, useSlideLayout } from '@gripp/shared-ui';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { AddFilesLinksButton } from './addFilesLinksButton';
import { FilesContext, MediaExtended } from './filesContext';
import { filesSharedStyles } from './filesSharedStyles';

type RightColumnProps = {
  assetId: string;
  searchKeyword: string;
  selectedCategoryId: string | undefined;
  onAllFilesLoaded: (files: MediaExtended[]) => void;
};

export const RightColumn = ({
  assetId,
  searchKeyword,
  selectedCategoryId,
  onAllFilesLoaded,
}: RightColumnProps) => {
  const { workspaceAsset, invalidateQuery } = useWorkspaceAsset(assetId);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const canEdit = account?.hasPermission(PermissionValues.asset.update);
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const { toggleThirdPanel } = useSlideLayout();
  const {
    setGalleryImages,
    setGalleryInitialIndex,
    setThirdPanelMode,
    setVideoMedia,
    isThirdPanelOpen,
    setIsThirdPanelOpen,
    filterPanelRequested,
    setFilterPanelRequested,
  } = useContext(FilesContext);

  const wrappedToggleThirdPanel = useCallback(() => {
    toggleThirdPanel();
    setIsThirdPanelOpen(!isThirdPanelOpen);
  }, [toggleThirdPanel, setIsThirdPanelOpen, isThirdPanelOpen]);

  // Handle filter panel toggle request from header
  useEffect(() => {
    if (filterPanelRequested) {
      setFilterPanelRequested(false);
      wrappedToggleThirdPanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPanelRequested]);

  const { product } = useProduct(asset?.product?.id || '');

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    onAllFilesLoaded(files);
    return files;
  }, [asset?.media, product?.media, onAllFilesLoaded]);

  const filteredFiles = useMemo(() => {
    let files = allFiles;

    if (selectedCategoryId) {
      files = files.filter((file) =>
        file.categories?.some((cat) => cat.id === selectedCategoryId)
      );
    }

    if (searchKeyword.trim()) {
      const query = searchKeyword.toLowerCase();
      files = files.filter((file) => file.name?.toLowerCase().includes(query));
    }

    return files;
  }, [allFiles, searchKeyword, selectedCategoryId]);

  const imageFiles = useMemo(
    () => filteredFiles.filter((f) => f.type === MediaType.Image),
    [filteredFiles]
  );

  const openDocumentOrAudio = useCallback(async (media: Media) => {
    if (media.type === MediaType.Document) {
      const documentUrl = `${EnvConfig.apiUrl}/document/${media.id}`;
      try {
        const response = await fetch(documentUrl, {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.downloadUrl) {
            const fileResponse = await fetch(data.downloadUrl);
            if (fileResponse.ok) {
              const fileBlob = await fileResponse.blob();
              const downloadLink = document.createElement('a');
              downloadLink.href = URL.createObjectURL(fileBlob);
              downloadLink.download = media.name || 'document';
              downloadLink.style.display = 'none';
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(downloadLink.href);
              return;
            }
            window.open(data.downloadUrl, '_blank');
            return;
          }
        }
      } catch {
        // fallback to opening the URL directly
      }
      window.open(documentUrl, '_blank');
    } else {
      const fileUrl = `${EnvConfig.mediaUrl}/media/${media.id}`;
      window.open(fileUrl, '_blank');
    }
  }, []);

  const handleMediaClick = useCallback(
    (media: Media) => {
      switch (media.type) {
        case MediaType.Image: {
          const images = imageFiles.map((file) => ({
            id: file.id,
            url: `${EnvConfig.mediaUrl}/images/${file.id}/original.${file.extension}`,
            name: file.name,
          }));
          const index = imageFiles.findIndex((f) => f.id === media.id);
          setGalleryImages(images);
          setGalleryInitialIndex(index >= 0 ? index : 0);
          setThirdPanelMode('gallery');
          if (!isThirdPanelOpen) {
            toggleThirdPanel();
            setIsThirdPanelOpen(true);
          }
          break;
        }
        case MediaType.Link: {
          if (media.url) {
            const normalizedUrl = normalizeUrl(media.url);
            window.open(normalizedUrl, '_blank');
          }
          break;
        }
        case MediaType.Document:
        case MediaType.Audio: {
          openDocumentOrAudio(media);
          break;
        }
        case MediaType.Video: {
          setVideoMedia(media);
          setThirdPanelMode('video');
          if (!isThirdPanelOpen) {
            toggleThirdPanel();
            setIsThirdPanelOpen(true);
          }
          break;
        }
      }
    },
    [
      imageFiles,
      isThirdPanelOpen,
      setGalleryImages,
      setGalleryInitialIndex,
      setIsThirdPanelOpen,
      setThirdPanelMode,
      setVideoMedia,
      toggleThirdPanel,
      openDocumentOrAudio,
    ]
  );

  if (!workspaceAsset) return null;

  return (
    <View style={filesSharedStyles.container}>
      <ScrollView
        showsVerticalScrollIndicator={true}
        style={filesSharedStyles.scrollView}
      >
        {filteredFiles.map((file: MediaExtended) => (
          <MediaListItem
            key={file.id}
            media={file}
            invalidateQuery={invalidateQuery}
            useNewCropCircle
            onClickMedia={handleMediaClick}
          />
        ))}
      </ScrollView>
      {asset && !isReadOnlyMode && (
        <AddFilesLinksButton
          asset={asset}
          invalidateQuery={invalidateQuery}
          disabled={!canEdit}
          useCropCircle
        />
      )}
    </View>
  );
};
